import {
  ResponderMapProvider,
  TourMapEdit,
  TourMapView,
  useResponderMap,
  ZonesProvider,
} from 'features/Responder';
import { PersonnelProvider } from 'features/Responder/features/Personnel';
import { SchedulesProvider } from 'features/Responder/features/Schedules/contexts/SchedulesContext';
import RunEventHistoryPanel from 'features/Responder/features/Schedules/features/RunEventHistory';
import ScheduleTelemetry from 'features/Responder/features/Schedules/features/ScheduleTelemetry';
import {
  CheckpointList,
  TourToolsPanel,
  TourBuilder as TourBuilderPanel,
  TourList,
  TourProvider,
  useTours,
} from 'features/Responder/features/Tours';
import useResponderDialog from 'features/Responder/hooks/useResponderDialog';
import ScheduleNudgePrompt from 'features/Responder/components/ScheduleNudgePrompt';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Group, Panel } from 'react-resizable-panels';
import { useNavigate } from 'react-router-dom';
import { ResizablePanelHandle } from 'utils/panelResizeUtils';

const normalizeNumberOrNull = (value) => {
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
};

const buildTourEditorSignature = ({ builderTourData, startLocation, checkpoints, routeData }) => {
  const normalizedStart = {
    address: startLocation?.address ?? '',
    placeId: startLocation?.placeId ?? '',
    lat: normalizeNumberOrNull(startLocation?.lat),
    lng: normalizeNumberOrNull(startLocation?.lng),
    zoneId: startLocation?.zoneId ?? startLocation?.geometry?.zoneId ?? null,
    useCurrent: !!startLocation?.useCurrent,
  };

  const normalizedCheckpoints = (Array.isArray(checkpoints) ? checkpoints : []).map((cp) => ({
    id: cp?.id ?? null,
    label: cp?.label ?? '',
    address: cp?.address ?? '',
    placeId: cp?.placeId ?? '',
    lat: normalizeNumberOrNull(cp?.lat),
    lng: normalizeNumberOrNull(cp?.lng),
    mode: cp?.mode ?? cp?.travelMode ?? '',
    radiusMeters: normalizeNumberOrNull(cp?.radiusMeters),
    stopTimeMinutes: normalizeNumberOrNull(cp?.stopTimeMinutes),
    timeWindowMinutes: normalizeNumberOrNull(cp?.timeWindowMinutes),
    timeWindowSeconds: normalizeNumberOrNull(cp?.timeWindowSeconds),
    geometry: cp?.geometry ?? null,
  }));

  const normalizedRouteData = routeData
    ? {
        polyline: routeData?.polyline ?? null,
        totalDuration: normalizeNumberOrNull(routeData?.totalDuration),
        totalDistanceMeters: normalizeNumberOrNull(routeData?.totalDistanceMeters),
        legs: (Array.isArray(routeData?.legs) ? routeData.legs : []).map((leg) => ({
          travelMode: leg?.travelMode ?? leg?.mode ?? '',
          duration: normalizeNumberOrNull(leg?.duration),
          distanceMeters: normalizeNumberOrNull(leg?.distanceMeters),
          polyline: leg?.polyline ?? leg?.encodedPolyline ?? null,
        })),
      }
    : null;

  return JSON.stringify({
    builderTourId: builderTourData?.id ?? null,
    builderTourName: builderTourData?.name ?? '',
    isLooped: typeof builderTourData?.isLooped === 'boolean' ? builderTourData.isLooped : null,
    startLocation: normalizedStart,
    checkpoints: normalizedCheckpoints,
    routeData: normalizedRouteData,
  });
};

const usePageHeight = () => {
  const pageRef = useRef(null);
  const [pageHeight, setPageHeight] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const updateHeight = () => {
      if (!pageRef.current) return;
      const topOffset = pageRef.current.getBoundingClientRect().top || 0;
      const nextHeight = Math.max(window.innerHeight - topOffset, 320);
      setPageHeight(nextHeight);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  return { pageRef, pageHeight };
};

const ReadOnlyTourLayout = () => {
  return (
    <Row className="flex-grow-1" style={{ minHeight: 0 }}>
      <Col className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
        <Group
          orientation="horizontal"
          className="d-flex w-100 flex-grow-1 h-100"
          style={{ minHeight: 0, height: '100%' }}
        >
          <Panel defaultSize={70} minSize={40} className="d-flex flex-column h-100">
            <Group
              orientation="vertical"
              className="d-flex flex-column h-100"
              style={{ minHeight: 0 }}
            >
              <Panel defaultSize={65} minSize={35} className="d-flex flex-column h-100">
                <div className="card border-0 shadow-lg bg-dark text-white h-100">
                  <div className="card-body rounded overflow-hidden p-0 h-100">
                    <TourMapView />
                  </div>
                </div>
              </Panel>

              <ResizablePanelHandle direction="horizontal" />

              <Panel defaultSize={35} minSize={25} className="d-flex flex-column h-100">
                <Group
                  orientation="horizontal"
                  className="d-flex flex-row h-100"
                  style={{ minHeight: 0 }}
                >
                  <Panel defaultSize={50} minSize={25} className="d-flex flex-column h-100">
                    <div className="card border-0 shadow-lg bg-dark text-white h-100">
                      <div
                        className="card-body h-100 d-flex flex-column p-2"
                        style={{ minHeight: 0 }}
                      >
                        <TourList />
                      </div>
                    </div>
                  </Panel>
                  <ResizablePanelHandle direction="vertical" />
                  <Panel defaultSize={50} minSize={25} className="d-flex flex-column h-100">
                    <div className="card border-0 shadow-lg bg-dark text-white h-100">
                      <div
                        className="card-body h-100 d-flex flex-column p-2"
                        style={{ minHeight: 0 }}
                      >
                        <CheckpointList maxHeight="100%" />
                      </div>
                    </div>
                  </Panel>
                </Group>
              </Panel>
            </Group>
          </Panel>

          <ResizablePanelHandle direction="vertical" />

          <Panel defaultSize={30} minSize={20} className="d-flex flex-column h-100">
            <Group
              orientation="vertical"
              className="d-flex flex-column h-100"
              style={{ minHeight: 0 }}
            >
              <Panel defaultSize={60} minSize={30} className="d-flex flex-column h-100">
                <RunEventHistoryPanel />
              </Panel>
              <ResizablePanelHandle direction="horizontal" />
              <Panel defaultSize={40} minSize={25} className="d-flex flex-column h-100">
                <div className="card border-0 shadow-lg bg-dark text-white h-100">
                  <div className="card-body h-100 d-flex flex-column p-2" style={{ minHeight: 0 }}>
                    <ScheduleTelemetry scrollHeight="100%" />
                  </div>
                </div>
              </Panel>
            </Group>
          </Panel>
        </Group>
      </Col>
    </Row>
  );
};

const EditTourLayout = ({ onPersistSuccess = () => {}, onCancelEdit = () => {} }) => {
  const { mapTool, setMapTool } = useTours();
  const { showTourTools, setShowTourTools } = useResponderMap();

  useEffect(() => {
    setShowTourTools(true);
  }, [setShowTourTools]);

  return (
    <Row className="flex-grow-1" style={{ minHeight: 0 }}>
      <Col className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
        <Group
          orientation="horizontal"
          className="d-flex w-100 flex-grow-1 h-100"
          style={{ minHeight: 0, height: '100%' }}
        >
          <Panel defaultSize={70} minSize={40} className="d-flex flex-column h-100">
            <Group
              orientation="vertical"
              className="d-flex flex-column h-100"
              style={{ minHeight: 0 }}
            >
              <Panel defaultSize={65} minSize={35} className="d-flex flex-column h-100">
                <div className="card border-0 shadow-lg bg-dark text-white h-100">
                  <div className="card-body rounded overflow-hidden p-0 h-100">
                    <div className="position-relative w-100 h-100">
                      <div className="w-100 h-100 rounded-top overflow-hidden">
                        <TourMapEdit />
                      </div>
                      {showTourTools && (
                        <div
                          className="position-absolute"
                          style={{
                            top: 16,
                            right: 16,
                            zIndex: 750,
                            width: 'min(320px, 30%)',
                            maxWidth: 'calc(100% - 32px)',
                          }}
                        >
                          <TourToolsPanel
                            mapTool={mapTool}
                            setMapTool={setMapTool}
                            showTourTools={showTourTools}
                            setShowTourTools={setShowTourTools}
                            stacked
                            fullWidth
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Panel>

              <ResizablePanelHandle direction="horizontal" />

              <Panel defaultSize={35} minSize={25} className="d-flex flex-column h-100">
                <Group
                  orientation="horizontal"
                  className="d-flex flex-row h-100"
                  style={{ minHeight: 0 }}
                >
                  <Panel defaultSize={50} minSize={25} className="d-flex flex-column h-100">
                    <div className="card border-0 shadow-lg bg-dark text-white h-100">
                      <div
                        className="card-body h-100 d-flex flex-column p-2"
                        style={{ minHeight: 0 }}
                      >
                        <ScheduleTelemetry scrollHeight="100%" />
                      </div>
                    </div>
                  </Panel>
                  <ResizablePanelHandle direction="vertical" />
                  <Panel defaultSize={50} minSize={25} className="d-flex flex-column h-100">
                    <div className="card border-0 shadow-lg bg-dark text-white h-100">
                      <div
                        className="card-body h-100 d-flex flex-column p-2"
                        style={{ minHeight: 0 }}
                      >
                        <CheckpointList maxHeight="100%" />
                      </div>
                    </div>
                  </Panel>
                </Group>
              </Panel>
            </Group>
          </Panel>

          <ResizablePanelHandle direction="vertical" />

          <Panel defaultSize={30} minSize={20} className="d-flex flex-column h-100">
            <TourBuilderPanel onPersistSuccess={onPersistSuccess} onCancelEdit={onCancelEdit} />
          </Panel>
        </Group>
      </Col>
    </Row>
  );
};

const ToursLayout = () => {
  const navigate = useNavigate();
  const { pageRef, pageHeight } = usePageHeight();
  const { confirm: showConfirm, dialogElement } = useResponderDialog();
  const {
    builderHydrationRef,
    builderTourData,
    checkpoints,
    resetBuilderState,
    routeData,
    startLocation,
    tourEditing,
    setTourEditing,
  } = useTours();
  const isEditMode = !!tourEditing;
  const [editSessionBaseline, setEditSessionBaseline] = useState(null);
  const [scheduleNudge, setScheduleNudge] = useState(null);
  const latestEditorSignatureRef = useRef('');
  const wasEditModeRef = useRef(false);
  const editorSignature = useMemo(
    () =>
      buildTourEditorSignature({
        builderTourData,
        startLocation,
        checkpoints,
        routeData,
      }),
    [builderTourData, startLocation, checkpoints, routeData],
  );

  useEffect(() => {
    // Tours page defaults to read mode on load (mirrors Zones behavior).
    setTourEditing(false);
  }, [setTourEditing]);

  useEffect(() => {
    latestEditorSignatureRef.current = editorSignature;
  }, [editorSignature]);

  useEffect(() => {
    if (!isEditMode) {
      setEditSessionBaseline(null);
      wasEditModeRef.current = false;
      return undefined;
    }
    if (!wasEditModeRef.current) {
      wasEditModeRef.current = true;
      const baselineTimer = window.setTimeout(() => {
        setEditSessionBaseline(latestEditorSignatureRef.current);
      }, 150);
      return () => window.clearTimeout(baselineTimer);
    }
    return undefined;
  }, [isEditMode]);

  const hasUnsavedTourChanges = useMemo(() => {
    if (!isEditMode) return false;
    if (!editSessionBaseline) return false;
    return editSessionBaseline !== editorSignature;
  }, [editSessionBaseline, editorSignature, isEditMode]);

  useEffect(() => {
    if (!hasUnsavedTourChanges) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedTourChanges]);

  const handleCancelEdit = useCallback(async () => {
    if (hasUnsavedTourChanges) {
      const shouldDiscard = await showConfirm({
        title: 'Discard tour changes',
        message: 'You have unsaved changes. Canceling will discard these changes. Continue?',
        confirmLabel: 'Discard changes',
        cancelLabel: 'Keep editing',
      });
      if (!shouldDiscard) return;
    }
    setTourEditing(false);
    if (builderHydrationRef) {
      builderHydrationRef.current = null;
    }
    if (typeof resetBuilderState === 'function') {
      resetBuilderState();
    }
  }, [builderHydrationRef, hasUnsavedTourChanges, resetBuilderState, setTourEditing, showConfirm]);

  const handlePersistSuccess = useCallback((persistPayload) => {
    setTourEditing(false);
    if (persistPayload?.mode !== 'new' || persistPayload?.savedTour?.id == null) return;
    setScheduleNudge({
      entityType: 'tour',
      entityId: persistPayload.savedTour.id,
      entityName: persistPayload.savedTour.name || '',
      entityData: persistPayload.savedTour,
    });
  }, [setTourEditing]);

  const handleNudgeConfirm = useCallback(() => {
    if (scheduleNudge?.entityId == null) return;
    navigate('/responder/schedules', {
      state: {
        responderSchedulePrefill: {
          entityType: 'tour',
          entityId: scheduleNudge.entityId,
          entityName: scheduleNudge.entityName || '',
          entityData: scheduleNudge.entityData || null,
          requestedAt: Date.now(),
          source: 'tour-create',
        },
      },
    });
    setScheduleNudge(null);
  }, [navigate, scheduleNudge]);

  const handleNudgeDismiss = useCallback(() => {
    setScheduleNudge(null);
  }, []);

  return (
    <div
      ref={pageRef}
      className="tour-builder-page py-4"
      style={{
        minHeight: 0,
        height: pageHeight ? `${pageHeight}px` : '100vh',
        background: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Container fluid className="d-flex flex-column" style={{ flex: 1, minHeight: 0 }}>
        {isEditMode ? (
          <EditTourLayout
            key="edit"
            onPersistSuccess={handlePersistSuccess}
            onCancelEdit={handleCancelEdit}
          />
        ) : (
          <ReadOnlyTourLayout key="view" />
        )}
      </Container>
      <ScheduleNudgePrompt
        open={!!scheduleNudge}
        entityType={scheduleNudge?.entityType}
        entityName={scheduleNudge?.entityName}
        onConfirm={handleNudgeConfirm}
        onDismiss={handleNudgeDismiss}
      />
      {dialogElement}
    </div>
  );
};

export default function Tours() {
  return (
    <PersonnelProvider>
      <TourProvider>
        <ZonesProvider>
          <SchedulesProvider>
            <ResponderMapProvider>
              <ToursLayout />
            </ResponderMapProvider>
          </SchedulesProvider>
        </ZonesProvider>
      </TourProvider>
    </PersonnelProvider>
  );
}
